import { useState, useRef } from "react";
import { Pencil, Camera } from "lucide-react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { EditBannerDialog } from "./EditBannerDialog";
import { getInstitutionLogoUrl, uploadInstitutionLogo, updateInstitution } from "@/Services/institutions.service";
import { useAuth } from "@/context/authContext";
import { usePermissions } from "@/hooks/usePermissions";
import { useQueryClient } from "@tanstack/react-query";
import type { Institution } from "../types";
import type { ElementType } from "react";

interface InstitutionBannerProps {
  institution: Institution;
  icon: ElementType;
  onGradientChange: (gradient: string) => void;
}

export function InstitutionBanner({
  institution,
  icon: Icon,
  onGradientChange,
}: InstitutionBannerProps) {
  const { user } = useAuth();
  const { can } = usePermissions();
  const canUploadLogo = can("institution.upload_logo");
  const canEditInstitution = can("institution.edit");
  const queryClient = useQueryClient();
  const [editOpen, setEditOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const gradient = institution.gradient ?? "linear-gradient(135deg, #6366f1 0%, #3b82f6 100%)";
  const logoUrl = getInstitutionLogoUrl(institution.logo_url ?? null);

  async function handleLogoUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !user) return;
    setUploading(true);
    try {
      const path = await uploadInstitutionLogo(institution.slug, file);
      await updateInstitution(institution.id, { logo_url: path }, user.id);
      await queryClient.invalidateQueries({ queryKey: ["institutions"] });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  return (
    <>
      <motion.div
        className="relative overflow-hidden rounded-3xl p-6 text-white shadow-lg md:p-8"
        style={{ background: gradient }}
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
      >
        <div className="pointer-events-none absolute inset-0 bg-black/5" />
        <div className="pointer-events-none absolute -right-10 -top-10 size-48 rounded-full bg-white/10" />
        <div className="pointer-events-none absolute -bottom-12 -right-4 size-64 rounded-full bg-white/5" />

        <div className="relative flex items-center justify-between gap-4">
          <div className="min-w-0">
            <div className="mb-2 flex items-center gap-3">
              {/* Logo / icon */}
              <div className="group relative flex size-12 shrink-0 items-center justify-center rounded-2xl bg-white/20 backdrop-blur-sm">
                {logoUrl ? (
                  <img src={logoUrl} alt={institution.name} className="size-full rounded-2xl object-cover" />
                ) : (
                  <Icon className="size-6 text-white" />
                )}
                {canUploadLogo && (
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="absolute inset-0 flex items-center justify-center rounded-2xl bg-black/40 opacity-0 transition-opacity group-hover:opacity-100 disabled:opacity-50"
                    title="Cambiar logo"
                  >
                    <Camera className="size-4 text-white" />
                  </button>
                )}
              </div>
              <h1 className="text-2xl font-bold leading-tight md:text-3xl">
                {institution.name}
              </h1>
            </div>
            {institution.description && (
              <p className="max-w-xl text-sm opacity-90 md:text-base">
                {institution.description}
              </p>
            )}
          </div>

          {canEditInstitution && (
            <Button
              variant="secondary"
              size="sm"
              className="shrink-0 gap-1.5 bg-white/20 text-white hover:bg-white/30"
              onClick={() => setEditOpen(true)}
            >
              <Pencil className="size-3.5" />
              Editar
            </Button>
          )}
        </div>
      </motion.div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleLogoUpload}
      />

      <EditBannerDialog
        isOpen={editOpen}
        onClose={() => setEditOpen(false)}
        currentGradient={gradient}
        onSave={(g) => {
          onGradientChange(g);
          setEditOpen(false);
        }}
      />
    </>
  );
}
